import { getStorageAdapter, getStorageBackendLabel } from './StorageAdapter';

export type SavedSettings = {
  alwaysRun: boolean;
  showSourceBadge: boolean;
  cameraZoom: number;
  textSpeed: number;
  uiMotion: boolean;
  interactionBubble: boolean;
  mobileControlScale: number;
  mobileControlOpacity: number;
  mobileControlLayout: 'standard' | 'reversed';
  hapticFeedback: boolean;
};
export type SavedSession = { sceneType: 'village' | 'house'; villageId?: string; houseId?: string; spawnX?: number; spawnY?: number; };

const SETTINGS_KEY = 'jrpg-launcher.settings.v1';
const SESSION_KEY = 'jrpg-launcher.session.v1';
const DEFAULT_SETTINGS: SavedSettings = {
  alwaysRun: false,
  showSourceBadge: true,
  cameraZoom: 1,
  textSpeed: 1,
  uiMotion: true,
  interactionBubble: true,
  mobileControlScale: 1,
  mobileControlOpacity: 0.75,
  mobileControlLayout: 'standard',
  hapticFeedback: true,
};
const storage = getStorageAdapter();

export class SaveStore {
  private static lastSessionJson = storage.getItem(SESSION_KEY);
  private static pendingSessionJson?: string;
  private static lastSessionWriteAt = 0;

  static loadSettings(): SavedSettings {
    try {
      const raw = storage.getItem(SETTINGS_KEY);
      if (!raw) return DEFAULT_SETTINGS;
      return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<SavedSettings>) };
    } catch { return DEFAULT_SETTINGS; }
  }
  static saveSettings(settings: SavedSettings) { storage.setItem(SETTINGS_KEY, JSON.stringify(settings)); }
  static loadSession(): SavedSession | undefined {
    try {
      const raw = storage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as SavedSession) : undefined;
    } catch { return undefined; }
  }
  static saveSession(session: SavedSession, immediate = false) {
    const json = JSON.stringify(session);
    if (json === this.lastSessionJson || json === this.pendingSessionJson) return;
    this.pendingSessionJson = json;

    const previousIdentity = this.getSessionIdentity(this.lastSessionJson);
    const nextIdentity = this.getSessionIdentity(json);
    const shouldWrite =
      immediate ||
      previousIdentity !== nextIdentity ||
      Date.now() - this.lastSessionWriteAt >= 750;

    if (shouldWrite) this.flushSession();
  }

  static flushSession() {
    if (!this.pendingSessionJson || this.pendingSessionJson === this.lastSessionJson) return;
    storage.setItem(SESSION_KEY, this.pendingSessionJson);
    this.lastSessionJson = this.pendingSessionJson;
    this.pendingSessionJson = undefined;
    this.lastSessionWriteAt = Date.now();
  }

  static clearSession() {
    this.pendingSessionJson = undefined;
    this.lastSessionJson = null;
    storage.removeItem(SESSION_KEY);
  }
  static getBackendLabel() { return getStorageBackendLabel(); }

  private static getSessionIdentity(json: string | null | undefined) {
    if (!json) return '';
    try {
      const session = JSON.parse(json) as SavedSession;
      return `${session.sceneType}:${session.villageId ?? ''}:${session.houseId ?? ''}`;
    } catch {
      return '';
    }
  }
}

export const defaultSettings = DEFAULT_SETTINGS;
